import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur-md border-b border-background-200/70 py-2.5"
          : "bg-foreground-950/60 backdrop-blur-sm py-3"
      }`}
    >
      <div className="w-full px-4 md:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-4">
          <button
            onClick={() => navigate("/")}
            className="font-heading font-bold text-lg md:text-xl cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
            style={{ color: scrolled ? "oklch(var(--foreground-950))" : "oklch(var(--background-50))" }}
          >
            MuleBuy Hub
          </button>

          <button
            onClick={() => navigate("/")}
            className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label font-medium cursor-pointer transition-colors whitespace-nowrap ${
              scrolled
                ? "bg-background-200/70 text-foreground-800 hover:bg-background-300/70"
                : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
            }`}
          >
            <i className="ri-home-4-line text-sm"></i>
            Home
          </button>

          <button
            onClick={() => navigate("/mulebuy-spreadsheet-guide")}
            className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-label font-medium cursor-pointer transition-colors whitespace-nowrap ${
              scrolled
                ? "bg-background-200/70 text-foreground-800 hover:bg-background-300/70"
                : "bg-background-50/15 text-background-50 hover:bg-background-50/25"
            }`}
          >
            <i className="ri-book-open-line text-sm"></i>
            Shopping Guide
          </button>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div ref={dropdownRef} className="relative hidden md:block">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 rounded-full border text-sm font-label font-medium cursor-pointer transition-colors whitespace-nowrap ${
                scrolled
                  ? "border-background-300/60 bg-background-100 text-foreground-800 hover:bg-background-200/70"
                  : "border-background-50/25 bg-background-50/10 text-background-50 hover:bg-background-50/20"
              }`}
            >
              <span>Shop by Category</span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2 rounded-full text-sm font-label font-medium cursor-pointer transition-colors whitespace-nowrap ${
              scrolled
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "bg-background-50 text-foreground-950 hover:bg-background-100"
            }`}
          >
            MuleBuy Spreadsheet
            <i className="ri-external-link-line text-sm"></i>
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-1.5 px-3 py-2 md:px-4 md:py-2 rounded-full text-sm font-label font-medium cursor-pointer transition-colors whitespace-nowrap ${
              scrolled
                ? "border border-foreground-300 text-foreground-900 hover:bg-foreground-50"
                : "border border-background-50/40 text-background-50 hover:bg-background-50/15"
            }`}
          >
            Buy It Now
            <i className="ri-external-link-line text-sm"></i>
          </a>

          <button
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setMobileCatOpen(false);
            }}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              scrolled
                ? "text-foreground-800 hover:bg-background-200/70"
                : "text-background-50 hover:bg-background-50/15"
            }`}
          >
            <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 mt-2">
          <div className="px-4 py-3 flex flex-col gap-2">
            <button
              onClick={() => {
                navigate("/");
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-800 text-sm font-label font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </button>
            <button
              onClick={() => {
                navigate("/mulebuy-spreadsheet-guide");
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-800 text-sm font-label font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-book-open-line text-base"></i>
              Shopping Guide
            </button>
          </div>

          <div className="border-t border-background-200/70 px-4 py-3">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-foreground-800 text-sm font-label font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-base"></i>
                Shop by Category
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="mt-2 space-y-1 pl-8">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-background-200/70 px-4 py-3 flex flex-col gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              MuleBuy Spreadsheet
              <i className="ri-external-link-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-2.5 rounded-full border border-foreground-300 text-foreground-800 text-sm font-label font-medium hover:bg-background-100 transition-colors cursor-pointer"
            >
              Buy It Now
              <i className="ri-external-link-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}